/**
 * Where a guided tour's outcome lives (organizer tours, 28 Oct 2026):
 *  - the account: `user.systemPreferences.tours[id] = { version, completedAt? | skippedAt? + lastStep? | dismissedAt? }`
 *  - a localStorage mirror `sf-tours` with the same shape: instant decisions, and the net when the
 *    PUT fails or the browser is in private mode (same spirit as `sf-welcome`) — the only store for
 *    a visitor who is not logged in (the home page tour).
 * A tour is done for a definition when either source holds a version ≥ the definition's (completed,
 * skipped or dismissed); `isTourWatched` (completed or skipped) drives the « not seen yet » dot of the
 * help menu. The invitation card only looks at `isTourDismissed` (William, 29 Oct 2026: the card comes
 * back at every page load, even after the tour was played, until « Don't show again for this page »)
 * and at the session marker below; a completion or a skip keeps an earlier `dismissedAt`. Bumping
 * `version` in the definition re-offers everything once.
 * Pure functions, storage injectable for tests; a throwing storage reads as empty.
 */
export const TOURS_KEY = 'sf-tours'

const storageOf = (storage) => {
    if (storage !== undefined) return storage
    try {
        return globalThis.localStorage
    } catch {
        return null
    }
}

export const readLocalTours = (storage) => {
    try {
        const raw = storageOf(storage)?.getItem(TOURS_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

export const writeLocalTours = (tours, storage) => {
    try {
        storageOf(storage)?.setItem(TOURS_KEY, JSON.stringify(tours || {}))
    } catch {
        /* private mode: the account copy is the one that lasts */
    }
}

/** The version of `id` recorded in a tours map (0 when absent or malformed). */
export const tourVersionSeen = (tours, id) => {
    const entry = tours && typeof tours === 'object' ? tours[id] : null
    const version = Number(entry?.version)
    return Number.isFinite(version) ? version : 0
}

/** True when any of the sources (account, local…) recorded this definition's version or a newer one. */
export const isTourDone = (def, ...sources) => sources.some((tours) => tourVersionSeen(tours, def.id) >= def.version)

const entryOf = (tours, id) => (tours && typeof tours === 'object' ? tours[id] : null)

/** True when a source holds « don't show again » for this version or newer: the invitation card stays away. */
export const isTourDismissed = (def, ...sources) =>
    sources.some((tours) => tourVersionSeen(tours, def.id) >= def.version && !!entryOf(tours, def.id)?.dismissedAt)

/** True when a source holds the version (or newer) as actually watched — completed or skipped, not just dismissed. */
export const isTourWatched = (def, ...sources) =>
    sources.some((tours) => {
        const entry = tours && typeof tours === 'object' ? tours[def.id] : null
        return tourVersionSeen(tours, def.id) >= def.version && !!(entry?.completedAt || entry?.skippedAt)
    })

/** The tours map with this definition's outcome written (ISO dates: the backend casts them). */
export const withTourResult = (tours, def, { skipped = false, dismissed = false, lastStep, at = new Date() } = {}) => {
    const stamp = at instanceof Date ? at.toISOString() : at
    let entry
    if (dismissed) entry = { version: def.version, dismissedAt: stamp }
    else if (skipped)
        entry = { version: def.version, skippedAt: stamp, ...(Number.isInteger(lastStep) ? { lastStep } : {}) }
    else entry = { version: def.version, completedAt: stamp }
    // replaying a dismissed tour from the menu never brings the card back
    const previous = entryOf(tours, def.id)
    if (!dismissed && previous?.dismissedAt && tourVersionSeen(tours, def.id) >= def.version) {
        entry.dismissedAt = previous.dismissedAt
    }
    return { ...(tours || {}), [def.id]: entry }
}

/** Local entries the account does not have yet (or has at an older version): what a one-shot sync sends. */
export const toursMissingOnServer = (local, server) => {
    const missing = {}
    Object.keys(local || {}).forEach((id) => {
        if (tourVersionSeen(local, id) > tourVersionSeen(server, id)) missing[id] = local[id]
    })
    return Object.keys(missing).length ? missing : null
}

/**
 * One invitation per browser session AND per tour (`sessionStorage` `sf-tour-auto:<id>`): a card
 * answered « Not now » on its page never pops back at the next tab click, while another page's still may.
 */
export const AUTO_KEY = 'sf-tour-auto'

export const autoKeyOf = (id) => `${AUTO_KEY}:${id}`

const sessionOf = (storage) => {
    if (storage !== undefined) return storage
    try {
        return globalThis.sessionStorage
    } catch {
        return null
    }
}

export const readAutoAttempted = (id, storage) => {
    try {
        return !!sessionOf(storage)?.getItem(autoKeyOf(id))
    } catch {
        return false
    }
}

export const markAutoAttempted = (id, storage) => {
    try {
        sessionOf(storage)?.setItem(autoKeyOf(id), '1')
    } catch {
        /* blocked storage: the account / local progress still prevents a loop */
    }
}
